import React, { useCallback } from 'react';
import { Form } from 'react-bootstrap';
import { HubOptionStateValue } from 'dorian-shared/types/branch/HubOption';
import type { ReplayCondition } from 'dorian-shared/types/branch/HubOption';
import { useMemoryIconsContext } from 'contexts/MemoryIconsContext/MemoryIconsContext';
import { CustomSelect } from '../../../../../CustomSelect';
import { MemoryIconField } from '../../../../../../Book/MemoryBank/MemoryFields/MemoryIconField';
import { parseGotoBranchId } from '../../shared/hubUtils';
import { BranchSelectItem } from '../../shared/types';
import {
  HUB_CARD_TEXT_MAX_LENGTH,
  HUB_CARD_TEXT_WARN_LENGTH,
} from '../../hubStepDefaults';

type ReplayResultRowProps = {
  condition: ReplayCondition;
  branches: BranchSelectItem[];
  currentBranchId: number | null;
  isDisabled: boolean;
  onChange: (updated: ReplayCondition) => void;
};

const STATE_OPTIONS: { value: HubOptionStateValue; label: string }[] = [
  { value: HubOptionStateValue.Hidden, label: 'Hidden' },
  { value: HubOptionStateValue.Disabled, label: 'Disabled' },
  { value: HubOptionStateValue.Active, label: 'Active' },
];

const STATE_BADGE_CLASS: Record<HubOptionStateValue, string> = {
  [HubOptionStateValue.Hidden]: 'replay-condition-card__badge--hidden',
  [HubOptionStateValue.Disabled]: 'replay-condition-card__badge--disabled',
  [HubOptionStateValue.Active]: 'replay-condition-card__badge--active',
};

export function ReplayResultRow({
  condition,
  branches,
  currentBranchId,
  isDisabled,
  onChange,
}: ReplayResultRowProps) {
  const { memoryIcons, createMemoryIcon } = useMemoryIconsContext();

  const handleStateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      onChange({
        ...condition,
        resultState: val === '' ? null : (val as HubOptionStateValue),
      });
    },
    [condition, onChange],
  );

  const handleIconChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const iconId = e.target.value ? Number(e.target.value) : null;
      onChange({ ...condition, resultIconId: iconId });
    },
    [condition, onChange],
  );

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...condition, resultText: e.target.value });
    },
    [condition, onChange],
  );

  const handleGotoChange = useCallback(
    (raw: unknown) => {
      onChange({ ...condition, resultGotoBranchId: parseGotoBranchId(raw) });
    },
    [condition, onChange],
  );

  const isHidden = condition.resultState === HubOptionStateValue.Hidden;
  const isDisabledState = condition.resultState === HubOptionStateValue.Disabled;
  const iconFieldDisabled = isDisabled || isHidden;
  const textFieldDisabled = isDisabled || isHidden;
  const gotoFieldDisabled = isDisabled || isHidden || isDisabledState;

  return (
    <div className="replay-condition-card__result" data-testid="replay-result-row">
      {/* State */}
      <div className="replay-condition-card__result-field">
        <span className="replay-condition-card__result-field-label">
          <span className="replay-condition-card__result-arrow">→</span> State
        </span>
        <Form.Control
          as="select"
          size="sm"
          className="replay-condition-card__state-select"
          value={condition.resultState ?? ''}
          onChange={handleStateChange}
          disabled={isDisabled}
          data-testid="result-state-select"
        >
          <option value="" disabled hidden>—</option>
          {STATE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Form.Control>
        {condition.resultState && (
          <span
            className={`replay-condition-card__badge ${STATE_BADGE_CLASS[condition.resultState] ?? ''}`}
            data-testid="result-state-badge"
          >
            {condition.resultState.toUpperCase()}
          </span>
        )}
      </div>

      {/* Icon */}
      <div className="replay-condition-card__result-field">
        <span className="replay-condition-card__result-field-label">
          <span className="replay-condition-card__result-arrow">→</span> Icon
        </span>
        <div className="icon-field-inline" style={isHidden ? { pointerEvents: 'none', opacity: 0.5 } : undefined}>
          <MemoryIconField
            id={`replay-result-icon-${condition.uuid}`}
            label=""
            memoryIcons={memoryIcons}
            onSave={createMemoryIcon}
            value={isHidden ? '' : (condition.resultIconId != null ? String(condition.resultIconId) : '')}
            onChange={handleIconChange}
            disabled={isDisabled}
            variant="outline-primary"
          />
        </div>
      </div>

      {/* Text */}
      <div className="replay-condition-card__result-field">
        <span className="replay-condition-card__result-field-label">
          <span className="replay-condition-card__result-arrow">→</span> Text
        </span>
        <div className="replay-condition-card__text-input-wrap">
          <Form.Control
            size="sm"
            type="text"
            className={`replay-condition-card__text-input${(condition.resultText?.length ?? 0) > HUB_CARD_TEXT_WARN_LENGTH ? ' text-warn' : ''}`}
            placeholder="Button label"
            value={condition.resultText ?? ''}
            onChange={handleTextChange}
            disabled={textFieldDisabled}
            maxLength={HUB_CARD_TEXT_MAX_LENGTH}
            data-testid="result-text-input"
          />
          <Form.Text className="char-limit-info-box" style={{ top: '-8px', bottom: 'auto' }}>
            {HUB_CARD_TEXT_WARN_LENGTH - (condition.resultText?.length ?? 0)}
          </Form.Text>
        </div>
      </div>

      {/* Go to */}
      <div className="replay-condition-card__result-field">
        <span className="replay-condition-card__result-field-label">
          <span className="replay-condition-card__result-arrow">→</span> Go to
        </span>
        <div className="replay-condition-card__goto-select">
          <CustomSelect
            disabled={gotoFieldDisabled}
            value={condition.resultGotoBranchId ?? ''}
            onChange={handleGotoChange}
            placeholder="—"
            args={[
              { id: -1, title: '[Create New Node]' },
              ...branches.filter((b) => b.id !== currentBranchId),
            ]}
            data={currentBranchId}
          />
        </div>
      </div>
    </div>
  );
}
